import React, { useContext, useState, useMemo } from 'react';
import Stats from './components/Stats';
import ReconciliationStats from './components/ReconciliationStats';
import CurrentMonthStats from './components/CurrentMonthStats';
import Header from './components/Header';
import useFetch from '../../../../hooks/useFetch';
import CustomerService from '../../../../services/customer.service';
import VendorService from '../../../../services/vendor.service';
import UserService from '../../../../services/user.service';
import PropertyService from '../../../../services/property.service';
import PayoutService from '../../../../services/payout.service';
import CommonService from '../../../../services/common.service';
import { AuthContext } from '../../../../contexts/AuthContext';
import { PermissionModulesEnum, PermissionActionsEnum } from '../../../../enums';
import { hasResourseAccess, formatDate, getYearLookupData } from '../../../../utils/methods';
import { GENERIC_LOOKUP_KEYS, DEFAULTS, VALIDATION_FIELD_MAPPING } from '../../../../utils/appConstant';
import Spinner from '../../../../components/Spinner';

const Dashboard: React.FC = () => {
  const { permissions, isSystemAdmin } = useContext(AuthContext);

  const [filters, setFilters] = useState({
    [VALIDATION_FIELD_MAPPING.month]: DEFAULTS.month(),
    [VALIDATION_FIELD_MAPPING.year]: DEFAULTS.year()
  });

  const [dropdownSearchData, setDropdownSearchData] = useState({
    [VALIDATION_FIELD_MAPPING.month]: '',
    [VALIDATION_FIELD_MAPPING.year]: ''
  });

  // Check permissions for each KPI type
  const canViewUsers = hasResourseAccess(isSystemAdmin, permissions, { accessibleTo: [{ [PermissionModulesEnum.Users]: [PermissionActionsEnum.View] }] });
  const canViewVendors = hasResourseAccess(isSystemAdmin, permissions, { accessibleTo: [{ [PermissionModulesEnum.Vendor]: [PermissionActionsEnum.View] }] });
  const canViewCustomers = hasResourseAccess(isSystemAdmin, permissions, { accessibleTo: [{ [PermissionModulesEnum.Customers]: [PermissionActionsEnum.View] }] });
  const canViewProperties = hasResourseAccess(isSystemAdmin, permissions, { accessibleTo: [{ [PermissionModulesEnum.Property]: [PermissionActionsEnum.View] }] });
  const canViewReconciliation = hasResourseAccess(isSystemAdmin, permissions, { accessibleTo: [{ [PermissionModulesEnum.Reconciliation]: [PermissionActionsEnum.View] }] });

  const { data: allMonths } = useFetch({
    service: CommonService,
    func: 'genericLookup',
    payload: { key: GENERIC_LOOKUP_KEYS.month, searchText: '' },
    dependency: []
  });

  const { data: months } = useFetch({
    service: CommonService,
    func: 'genericLookup',
    payload: { key: GENERIC_LOOKUP_KEYS.month, searchText: dropdownSearchData[VALIDATION_FIELD_MAPPING.month] },
    dependency: [dropdownSearchData[VALIDATION_FIELD_MAPPING.month]]
  });

  const yearLookup = getYearLookupData();

  const { startDate, endDate, monthName } = useMemo(() => {
    const year = parseInt(filters[VALIDATION_FIELD_MAPPING.year]);
    const month = parseInt(filters[VALIDATION_FIELD_MAPPING.month]);
    const start = new Date(year, month - 1, 1);
    const end = new Date(year, month, 0);

    const mName = allMonths?.find((m: any) => m.id == filters[VALIDATION_FIELD_MAPPING.month])?.name || start.toLocaleString('default', { month: 'long' });

    return {
      startDate: formatDate(start),
      endDate: formatDate(end),
      monthName: mName
    };
  }, [filters, allMonths]);

  const monthOptions = useMemo(() => {
    const options = [];
    const today = new Date();

    // Past 5 months
    for (let i = 5; i > 0; i--) {
      const d = new Date(today.getFullYear(), today.getMonth() - i, 1);
      const monthStr = String(d.getMonth() + 1).padStart(2, '0');
      options.push({
        label: `${d.toLocaleString('default', { month: 'long' })} ${d.getFullYear()}`,
        value: `${monthStr}-${d.getFullYear()}`
      });
    }

    // Current month
    const currentMonthStr = String(today.getMonth() + 1).padStart(2, '0');
    options.push({
      label: `${today.toLocaleString('default', { month: 'long' })} ${today.getFullYear()}`,
      value: `${currentMonthStr}-${today.getFullYear()}`
    });

    // Next 2 months
    for (let i = 1; i <= 2; i++) {
      const d = new Date(today.getFullYear(), today.getMonth() + i, 1);
      const monthStr = String(d.getMonth() + 1).padStart(2, '0');
      options.push({
        label: `${d.toLocaleString('default', { month: 'long' })} ${d.getFullYear()}`,
        value: `${monthStr}-${d.getFullYear()}`
      });
    }
    return options;
  }, []);

  const handleMonthYearChange = (e: any) => {
    const [month, year] = e.target.value.split('-');
    setFilters(prev => ({
      ...prev,
      [VALIDATION_FIELD_MAPPING.month]: month,
      [VALIDATION_FIELD_MAPPING.year]: year
    }));
  };

  const { data: vendorKpis, loading: loadingVendorKpis }: any = useFetch({
    service: VendorService,
    func: 'kpis',
    payload: null,
    dependency: [],
    skip: !canViewVendors
  });

  const { data: userKpis, loading: loadingUserKpis }: any = useFetch({
    service: UserService,
    func: 'kpis',
    payload: null,
    dependency: [],
    skip: !canViewUsers
  });

  const { data: customerKpis, loading: loadingCustomerKpis }: any = useFetch({
    service: CustomerService,
    func: 'kpis',
    payload: null,
    dependency: [],
    skip: !canViewCustomers
  });

  const { data: propertyKpis, loading: loadingPropertyKpis }: any = useFetch({
    service: PropertyService,
    func: 'kpis',
    payload: null,
    dependency: [],
    skip: !canViewProperties
  });

  const [reconciliationKpis, setReconciliationKpis] = useState<any>(null);

  const { loading: loadingReconciliationKpis }: any = useFetch({
    service: PayoutService,
    func: 'list',
    payload: {
      startDate,
      endDate,
      page: 1,
      perPage: 1
    },
    dependency: [startDate, endDate],
    skip: !canViewReconciliation,
    onSuccess: (response: any) => {
      setReconciliationKpis(response?.kpis);
    }
  });

  const handleDropdownSearchChange = (e: any, name: string) => {
    setDropdownSearchData((prev) => ({ ...prev, [name]: e.target.value }));
  };

  const handleInputChange = (e: any, name: string) => {
    setFilters((prev) => ({ ...prev, [name]: e.target.value }));
  };

  const isStatsLoading = loadingVendorKpis || loadingUserKpis || loadingCustomerKpis || loadingPropertyKpis;

  let statsContent;

  if (isStatsLoading) {
    statsContent = (
      <div className="d-flex justify-content-center align-items-center" style={{ minHeight: '100px', marginTop: '20px' }}>
        <Spinner />
      </div>
    );
  } else {
    statsContent = (
      <Stats
        data={{
          totalUsers: userKpis?.total_users,
          totalVendors: vendorKpis?.total_vendors,
          totalCustomers: customerKpis?.total_customers,
          totalProperties: propertyKpis?.total_properties,
          myProperties: propertyKpis?.my_properties
        }}
      />
    );
  }

  let reconciliationStatsContent;

  if (loadingReconciliationKpis) {
    reconciliationStatsContent = (
      <div className="d-flex justify-content-center align-items-center" style={{ minHeight: '100px', marginTop: '20px' }}>
        <Spinner />
      </div>
    );
  } else if (canViewReconciliation) {
    reconciliationStatsContent = (
      <ReconciliationStats data={reconciliationKpis} />
    );
  }

  return (
    <>
      <Header
        filters={filters}
        dropdownSearchData={dropdownSearchData}
        months={months}
        allMonths={allMonths}
        yearLookup={yearLookup}
        onSearch={handleDropdownSearchChange}
        onChange={handleInputChange}
      />

      <CurrentMonthStats
        startDate={startDate}
        endDate={endDate}
        monthName={monthName}
        monthOptions={monthOptions}
        currentMonthYear={`${filters[VALIDATION_FIELD_MAPPING.month]}-${filters[VALIDATION_FIELD_MAPPING.year]}`}
        onMonthYearChange={handleMonthYearChange}
      />

      {reconciliationStatsContent}
      {statsContent}
    </>
  );
};

export default Dashboard;
